// Settings → Security → Overview: the health card (DESIGN-security.md, step 2 — Rob 25/10/2026).
// Admins only, like the rest of the page. Four tiles (1A); one tile's names open underneath.
//
// ⚠️ THE LISTS ARE THE SERVER'S (`services/security_health.py`) — who counts, and whether Remind
// is allowed. This only lays them out and words the dates (`lib/securityHealth.js`).
//
// ⚠️ NO MONEY ON THE 90-DAY TILE (Rob's 2A). Archiving somebody doesn't change the bill — only
// deleting them does — so "they cost £3 a month" next to "archive them" would promise a saving
// that never comes. `securityHealth.checks.mjs` fails if a £ appears here.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { securityApi, usersApi } from '../../lib/api';
import {
  invitedWords, inviteButton, lastSignedInWords, lockedUntilWords, lockoutWhen, lookAtWords,
  people, remindedWords,
} from '../../lib/securityHealth';

const TONES = {
  warn: 'border-amber-300 bg-amber-50/60 dark:bg-amber-500/10 dark:border-amber-500/40',
  bad: 'border-red-200 bg-red-50/60 dark:bg-red-500/10 dark:border-red-500/40',
  ok: 'border-border bg-card',
};

function Tile({ tone, big, bigOk, children, action, testid }) {
  return (
    <div className={`rounded-xl border p-3 flex flex-col ${TONES[tone]}`} data-testid={testid}>
      <div className={`text-2xl font-extrabold leading-tight ${bigOk ? 'text-green-700 dark:text-green-400' : ''}`}>{big}</div>
      <div className="text-xs text-muted-foreground mt-0.5 mb-2 flex-1">{children}</div>
      <div>{action}</div>
    </div>
  );
}

function AllGood() {
  return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[11px] font-bold bg-green-500/15 text-green-700 dark:text-green-400">
      All good
    </span>
  );
}

function Row({ name, note, children, testid }) {
  return (
    <div className="flex items-center gap-3 py-1.5 text-sm" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <span className="font-medium">{name}</span>
        {note && <span className="text-muted-foreground"> · {note}</span>}
      </div>
      {children}
    </div>
  );
}

export default function SecurityHealth() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(null);         // which tile's names are showing
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    securityApi.health()
      .then((r) => { setData(r.data); setFailed(false); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That didn’t work. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const remind = (a) => act(`remind-${a.id}`, () => securityApi.remind(a.id),
    `${a.name.split(' ')[0]} has been reminded`);
  const unlock = (p) => act(`unlock-${p.id}`, () => securityApi.unlock(p.id),
    `${p.name.split(' ')[0]} can sign in again`);
  const invite = (p) => act(`invite-${p.id}`, () => usersApi.sendInvite(p.id),
    `Invitation sent to ${p.name.split(' ')[0]}`);
  const inviteAll = (list) => act('invite-all', async () => {
    // One at a time, through the same invite the Employees page uses.
    for (const p of list) { await usersApi.sendInvite(p.id); }
  }, `Invitations sent to ${people(list.length)}`);

  if (failed) {
    return <div className="card-hive p-6"><p className="text-sm text-muted-foreground">Couldn't load the security check. Please refresh the page.</p></div>;
  }
  if (!data) {
    return <div className="card-hive p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const { admins, inactive, never_signed_in: never, locked } = data;
  const without = admins.without || [];
  const quiet = inactive.people || [];
  const nev = never.people || [];
  const chip = lookAtWords(data.to_look_at);
  const toggle = (k) => setOpen(open === k ? null : k);
  const seeWho = (k) => (
    <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => toggle(k)}
            data-testid={`health-see-${k}`}>
      {open === k ? 'Hide ▴' : 'See who ▾'}
    </Button>
  );

  // Tile 1: the one admin without two-factor gets a button straight on the tile.
  const onlyOne = without.length === 1 ? without[0] : null;
  let twofactorAction;
  if (!without.length) twofactorAction = <AllGood />;
  else if (onlyOne && onlyOne.is_me) {
    twofactorAction = <Link to="/profile" className="text-xs underline font-medium">Set it up on your Profile</Link>;
  } else if (onlyOne) {
    twofactorAction = (
      <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs"
              disabled={!onlyOne.can_remind || busy === `remind-${onlyOne.id}`}
              onClick={() => remind(onlyOne)} data-testid="health-remind-tile">
        {busy === `remind-${onlyOne.id}` && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
        {onlyOne.can_remind ? `Remind ${onlyOne.name.split(' ')[0]}` : remindedWords(onlyOne.reminded_at)}
      </Button>
    );
  } else twofactorAction = seeWho('twofactor');

  return (
    <div className="card-hive p-6 mb-4" data-testid="security-health">
      <div className="flex items-center gap-3">
        <h3 className="font-semibold flex-1">How secure is {data.company_name || 'your company'}?</h3>
        {chip
          ? <span className="rounded-full px-2.5 py-0.5 text-xs font-bold bg-amber-500/15 text-amber-700 dark:text-amber-400">{chip}</span>
          : <AllGood />}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mt-3">
        <Tile testid="health-tile-twofactor" tone={without.length ? 'warn' : 'ok'} bigOk={!without.length}
              big={`${admins.with_twofactor} of ${admins.total}`} action={twofactorAction}>
          admins use two-factor.
          {onlyOne && <> <b className="text-foreground">{onlyOne.is_me ? 'You' : onlyOne.name}</b> {onlyOne.is_me ? 'don’t' : 'doesn’t'}.</>}
          {without.length > 1 && <> {without.length} don’t.</>}
        </Tile>

        <Tile testid="health-tile-inactive" tone={quiet.length ? 'bad' : 'ok'} bigOk={!quiet.length}
              big={quiet.length} action={quiet.length ? seeWho('inactive') : <AllGood />}>
          {quiet.length === 1 ? 'person hasn’t' : 'people haven’t'} signed in for {inactive.days} days.
          {quiet.length > 0 && ' If they’ve left, archive them so they can’t get in.'}
        </Tile>

        <Tile testid="health-tile-never" tone={nev.length ? 'warn' : 'ok'} bigOk={!nev.length}
              big={nev.length} action={nev.length ? seeWho('never') : <AllGood />}>
          {nev.length === 1 ? 'person has' : 'people have'} an email address but {nev.length === 1 ? 'has' : 'have'} never signed in.
        </Tile>

        <Tile testid="health-tile-locked" tone={locked.now.length ? 'bad' : 'ok'} bigOk={!locked.now.length}
              big={locked.now.length}
              action={locked.now.length || locked.recent.length ? seeWho('locked') : <AllGood />}>
          locked out right now.{' '}
          {locked.recent.length === 0 ? `None in the last ${locked.recent_days} days.`
            : `${locked.recent.length === 1 ? '1 lockout' : `${locked.recent.length} lockouts`} in the last ${locked.recent_days} days.`}
        </Tile>
      </div>

      {open === 'twofactor' && (
        <div className="border-t border-border mt-3 pt-2" data-testid="health-list-twofactor">
          {without.map((a) => (
            <Row key={a.id} name={a.name} note={a.is_me ? 'that’s you' : remindedWords(a.reminded_at)}>
              {a.is_me
                ? <Link to="/profile" className="text-xs underline font-medium">Set it up on your Profile</Link>
                : (
                  <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs"
                          disabled={!a.can_remind || busy === `remind-${a.id}`} onClick={() => remind(a)}>
                    {busy === `remind-${a.id}` && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    {a.can_remind ? 'Remind' : 'Reminded'}
                  </Button>
                )}
            </Row>
          ))}
        </div>
      )}

      {open === 'inactive' && (
        <div className="border-t border-border mt-3 pt-2" data-testid="health-list-inactive">
          {quiet.map((p) => <Row key={p.id} name={p.name} note={lastSignedInWords(p.last_at)} />)}
          <div className="flex items-center gap-3 pt-2">
            <p className="text-xs text-muted-foreground flex-1">
              Somebody who books leave through their manager, or approves from Teams or Slack, may
              never need to sign in — only archive people who have left.
            </p>
            <Link to="/employees"><Button size="sm" variant="outline" className="h-7 px-2.5 text-xs">Open Employees</Button></Link>
          </div>
        </div>
      )}

      {open === 'never' && (
        <div className="border-t border-border mt-3 pt-2" data-testid="health-list-never">
          {nev.map((p) => (
            <Row key={p.id} name={p.name} note={invitedWords(p.invited_at, p.invite_expired)} testid="health-never-row">
              <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs"
                      disabled={busy === `invite-${p.id}` || busy === 'invite-all'} onClick={() => invite(p)}>
                {busy === `invite-${p.id}` && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                {inviteButton(p.invited_at)}
              </Button>
            </Row>
          ))}
          {nev.length > 1 && (
            <div className="flex justify-end pt-2">
              <Button size="sm" className="h-7 px-2.5 text-xs" disabled={busy === 'invite-all'}
                      onClick={() => inviteAll(nev)} data-testid="health-invite-all">
                {busy === 'invite-all' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Send to all {nev.length}
              </Button>
            </div>
          )}
        </div>
      )}

      {open === 'locked' && (
        <div className="border-t border-border mt-3 pt-2" data-testid="health-list-locked">
          {locked.now.map((p) => (
            <Row key={p.id} name={p.name} note={lockedUntilWords(p.until)}>
              <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs"
                      disabled={busy === `unlock-${p.id}`} onClick={() => unlock(p)}>
                {busy === `unlock-${p.id}` && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Unlock
              </Button>
            </Row>
          ))}
          {locked.recent.length > 0 && (
            <>
              <p className="text-[11px] font-bold tracking-wide uppercase text-muted-foreground mt-2 mb-0.5">
                Lockouts in the last {locked.recent_days} days
              </p>
              {locked.recent.map((p, i) => <Row key={`${p.id}-${i}`} name={p.name} note={lockoutWhen(p.at)} />)}
            </>
          )}
        </div>
      )}
    </div>
  );
}
